import Link from "next/link";
import Image from "next/image";
import { Search, ShoppingCart, Menu, ArrowRight } from "lucide-react";
import LoadingSpinner from "@/components/LoadingSpinner";
import BannerSkeleton from "./_bannerSkeleton";
import HeaderBlogSSR from "./headerBlog.server";
import HeaderSellerAcademySSR from "./headerSellerAcademy.server";
import is_seller_academy_path from "./_isSellerAcademyPath";
import HeaderAdsSSR from "./headerAds.server";
import is_ads_path from "./_isAdsPath";

// مسیرهای لیست محصول؛ هدرشان در موبایل نوار جستجو می‌گیرد
const PRODUCT_LIST_ROUTES = ["/search", "/products", "/category", "/brand", "/collection"];

const is_product_list_route = (pathname = "") =>
    PRODUCT_LIST_ROUTES.some(
        (route) => pathname === route || pathname.startsWith(route + "/")
    );

export default function HeaderSSR({ pathname, banner_count = 0 }) {


    if (is_ads_path(pathname)) return <HeaderAdsSSR pathname={pathname} />;

    if (is_seller_academy_path(pathname)) return <HeaderSellerAcademySSR banner_count={banner_count} />;

    if (pathname.startsWith("/blog")) return <HeaderBlogSSR pathname={pathname} banner_count={banner_count} />;

    const isSingleProduct = pathname.startsWith("/product/");
    const isCart = pathname.startsWith("/cart");
    const isAccount = pathname.startsWith("/account/");
    const isProductList = is_product_list_route(pathname);
    const isAmazingPage = pathname === "/landing/amazing-offers/";
    const hasMobileBackButton =
        isAmazingPage ||
        pathname.startsWith("/contact-us") ||
        pathname.startsWith("/about-us");

    return (
        <header dir="rtl" className="relative w-full">
            <div className="fixed top-0 right-0 left-0 z-40 bg-jmt-white border-b border-jmt-neutral-100">

                {banner_count > 0 && <BannerSkeleton count={banner_count} />}

                {/* Mobile */}
                <div className="block lg:hidden">
                    {isCart || isAccount || isProductList ? null

                        : isSingleProduct ? (
                            <section className="border-b border-jmt-neutral-200">
                                <div className="container">
                                    <div className="flex items-center justify-between py-1">
                                        <div className="flex items-center" >
                                            <ArrowRight size={24} />
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center justify-center w-10 h-10">
                                                <LoadingSpinner />
                                            </div>
                                            <div className="flex items-center justify-center w-10 h-10">
                                                <LoadingSpinner />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        ) : (
                            <section className="w-full bg-jmt-white px-4 py-3">
                                <div className="flex items-center justify-between gap-4">

                                    {hasMobileBackButton && (
                                        <div className="flex items-center text-jmt-neutral-900">
                                            <ArrowRight size={24} />
                                        </div>
                                    )}

                                    {/* Mobile Search Bar Design */}
                                    <div className="flex-1 flex items-center bg-jmt-neutral-100 rounded-md px-3 py-2.5 gap-2">
                                        <Search
                                            size={24}
                                            className="text-jmt-neutral-400" />
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-jmt-neutral-600 text-sm font-bold">
                                                جستجو در
                                            </span>
                                            <div className="relative w-15 h-4">
                                                <Image
                                                    src="/images/logo/logoBlack.png"
                                                    unoptimized
                                                    alt="لوگو ژومنت"
                                                    className="object-contain w-full h-full"
                                                    width={150}
                                                    height={40}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}
                </div>

                {/* Desktop */}
                <div className="hidden lg:block">
                    <section className="w-full bg-jmt-white px-[22px]">
                        {/* Row 1: Logo, Search Bar, Auth & Cart */}
                        <div className="w-full flex items-center justify-between py-4 gap-8 h-20">

                            {/* Right Side: Logo & Search Bar */}
                            <div className="flex items-center justify-start flex-1 gap-8">
                                {/* Logo */}
                                <Link href="/" className="shrink-0">
                                    <div className="w-[150px] h-10 relative flex items-center">
                                        <Image
                                            src="/images/logo/logoBlack.png"
                                            unoptimized
                                            alt="لوگو ژومنت"
                                            width={150}
                                            height={40}
                                            priority
                                            className="object-contain w-auto h-10"
                                        />
                                    </div>
                                </Link>

                                {/* Desktop Search Bar */}
                                <div className="w-full max-w-[620px] h-11 bg-jmt-neutral-100 rounded-lg px-4 gap-3 flex items-center text-jmt-neutral-400 border border-transparent">
                                    <Search size={20} className="text-jmt-neutral-400 shrink-0" />
                                    <span className="text-sm font-normal text-jmt-neutral-400 select-none">
                                        جستجو
                                    </span>
                                </div>
                            </div>

                            {/* Left Side: Auth & Cart */}
                            <div className="flex items-center justify-end gap-4 shrink-0">
                                {/* Auth Button */}
                                <LoadingSpinner size={22} />

                                {/* Cart Button */}
                                <div className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-jmt-neutral-100 transition-colors">
                                    <ShoppingCart size={22} className="text-jmt-neutral-800" />
                                </div>
                            </div>
                        </div>

                        {/* Row 2: Navbar Menu Items */}
                        <nav className="w-full flex items-center justify-start gap-x-7 py-4 text-sm text-jmt-neutral-700">

                            <div className="flex items-center gap-1.5 hover:text-jmt-main transition-colors">
                                <Menu size={20} className="text-jmt-neutral-900" />
                                <span>دسته‌بندی محصولات</span>
                            </div>

                            <div className="bg-jmt-neutral-100 h-5 w-[110px] xl:w-[145px] rounded-md" />
                            <div className="bg-jmt-neutral-100 h-5 w-[110px] xl:w-[145px] rounded-md" />
                            <div className="bg-jmt-neutral-100 h-5 w-[110px] xl:w-[145px] rounded-md" />
                            <div className="bg-jmt-neutral-100 h-5 w-[110px] xl:w-[145px] rounded-md" />
                            <div className="bg-jmt-neutral-100 h-5 w-[110px] xl:w-[145px] rounded-md" />

                        </nav>
                    </section>
                </div>
            </div>

        </header>
    );
}
